import { jwtDecode } from 'jwt-decode';

/**
 * ניתוק אוטומטי של סשן שאינו פעיל (HAT-1092).
 *
 * הטוקן שהשרת מנפיק חי 30 יום (SecurityHelper.GenerateUserToken), והלקוח שמר
 * את המשתמש ב-localStorage בלי לבדוק את תוקפו אפילו פעם אחת. התוצאה שדווחה:
 * מי שנכנס שוב אחרי שבועיים נכנס ישר פנימה בלי שם משתמש וסיסמה.
 *
 * כאן נאכפים שני הגבולות שחסרו - חוסר פעילות (ברירת מחדל 60 דקות), ותוקף הטוקן עצמו.
 */

const LAST_ACTIVITY_KEY = 'sessionLastActivityAt';
const TIMED_OUT_FLAG_KEY = 'sessionTimedOut';
const REMEMBER_ME_KEY = 'sessionRememberMeUntil';

/**
 * Vite --mode dev (CI) רץ עם StrictMode: LoginPage עולה, נורד, ועולה שוב.
 * consume שמוחק מ-sessionStorage ב-effect הראשון משאיר את הרינדור השני בלי
 * הודעה. ה-latch חי במודול עד התחברות מוצלחת.
 */
let timedOutNoticeLatch = false;

const DEFAULT_IDLE_TIMEOUT_MINUTES = 60;

/**
 * אירועי DOM שנחשבים נוכחות. מאזינים ב-capture על document כדי שגלילה
 * במכולה פנימית (overflow בטבלאות / אזור ניהול) תיספר, לא רק גלילת החלון.
 */
export const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

const resolveTimeoutMinutes = (): number => {
  const configured = Number(import.meta.env.VITE_APP_SESSION_IDLE_TIMEOUT_MINUTES);
  return Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_IDLE_TIMEOUT_MINUTES;
};

export const IDLE_TIMEOUT_MS = resolveTimeoutMinutes() * 60 * 1000;

/** חלון האזהרה שלפני הניתוק - הזדמנות לסיים טופס פתוח במקום לאבד אותו. */
export const WARNING_BEFORE_MS = Math.min(2 * 60 * 1000, IDLE_TIMEOUT_MS / 2);

/** תדירות הבדיקה. צפוף מספיק כדי שהספירה לאחור תיראה חיה, וזול. */
export const CHECK_INTERVAL_MS = 1000;

/** אין טעם לכתוב ל-localStorage על כל הקלדה - מספיק פעם בכמה שניות. */
export const ACTIVITY_WRITE_THROTTLE_MS = 5000;

export const LAST_ACTIVITY_STORAGE_KEY = LAST_ACTIVITY_KEY;

export const readLastActivity = (): number | null => {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const writeLastActivity = (timestamp: number = Date.now()): void => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(timestamp));
  } catch {
    // מצב פרטי / אחסון חסום - הסשן פשוט לא ישרוד רענון, וזה הכיוון הבטוח.
  }
};

export const clearLastActivity = (): void => {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    // ראו writeLastActivity
  }
};

/** סימון שהניתוק היה אוטומטי, כדי שעמוד ההתחברות יסביר למה ולא ייראה כתקלה. */
export const markSessionTimedOut = (): void => {
  timedOutNoticeLatch = true;
  try {
    sessionStorage.setItem(TIMED_OUT_FLAG_KEY, '1');
  } catch {
    // ההודעה היא נחמדות, לא תנאי לניתוק
  }
};

/**
 * האם להציג את הודעת הניתוק. לא מאפס את ה-latch — רינדור שני של StrictMode
 * עדיין רואה אותה. איפוס רק ב-clearSessionTimedOutNotice (אחרי התחברות).
 */
export const consumeSessionTimedOutFlag = (): boolean => {
  try {
    if (sessionStorage.getItem(TIMED_OUT_FLAG_KEY) === '1') {
      timedOutNoticeLatch = true;
    }
  } catch {
    // latch מספיק אם האחסון חסום
  }
  return timedOutNoticeLatch;
};

export const clearSessionTimedOutNotice = (): void => {
  timedOutNoticeLatch = false;
  try {
    sessionStorage.removeItem(TIMED_OUT_FLAG_KEY);
  } catch {
    // ראו markSessionTimedOut
  }
};

/**
 * האם הטוקן פג. טוקן חסר או פגום נחשב פג: עדיף לבקש התחברות מחדש מאשר
 * להשאיר מעטפת "מחובר" שכל קריאה ממנה ממילא תיפול על 401.
 */
export const isTokenExpired = (token?: string | null): boolean => {
  if (!token) return true;
  try {
    const { exp } = jwtDecode(token);
    return exp === undefined || exp <= Date.now() / 1000;
  } catch {
    return true;
  }
};

/** משך "זכור אותי" — שבוע מהרגע שהמשתמש בחר בכך בהתחברות. */
export const REMEMBER_ME_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

export const REMEMBER_ME_STORAGE_KEY = REMEMBER_ME_KEY;

const readRememberMeUntil = (): number | null => {
  try {
    const raw = localStorage.getItem(REMEMBER_ME_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/** האם "זכור אותי" פעיל — עדיין בתוך חלון השבוע מהאופט-אין. */
export const isRememberMeActive = (): boolean => {
  const until = readRememberMeUntil();
  return until !== null && Date.now() < until;
};

/** מפעיל "זכור אותי" מההתחברות הנוכחית — שבוע מהרגע הזה. */
export const setRememberMe = (timestamp: number = Date.now()): void => {
  try {
    localStorage.setItem(REMEMBER_ME_KEY, String(timestamp + REMEMBER_ME_DURATION_MS));
  } catch {
    // מצב פרטי / אחסון חסום — ניתוק idle רגיל ימשיך לפעול.
  }
};

/** מנקה "זכור אותי" — בהתנתקות מפורשת או כשהמשתמש לא בוחר בכך. */
export const clearRememberMe = (): void => {
  try {
    localStorage.removeItem(REMEMBER_ME_KEY);
  } catch {
    // ראו setRememberMe
  }
};

/** סשן ששכב יותר מדי זמן ללא פעילות - גם אם הדפדפן נסגר ונפתח בינתיים. */
export const isSessionStale = (): boolean => {
  if (isRememberMeActive()) return false;
  const lastActivity = readLastActivity();
  return lastActivity !== null && Date.now() - lastActivity >= IDLE_TIMEOUT_MS;
};
